import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useApplication } from '@/Components/ApplicationProvider';
import { useKeyboardService } from '@/Components/KeyboardServiceProvider';
import { keyboardStringForShortcut, UNIVERSAL_SEARCH_TOGGLE_CASE_SENSITIVE, UNIVERSAL_SEARCH_TOGGLE_REPLACE_MODE, UNIVERSAL_TOGGLE_SEARCH, } from '@standardnotes/ui-services';
import { UniversalSearchController } from '../../UniversalSearch/UniversalSearchController';
import { registerUniversalSearchKeyboardHandlers } from '../../UniversalSearch/registerUniversalSearchKeyboardHandlers';
import { createPlainEditorUniversalSearchProvider } from '../../UniversalSearch/providers/PlainEditorUniversalSearchProvider';
import { UniversalSearchShell } from '../../UniversalSearch/UniversalSearchShell';
import { PlainEditor } from '../PlainEditor';
export const PlainEditorSearchContainer = observer(forwardRef(function PlainEditorSearchContainer(props, ref) {
    var _a;
    const application = useApplication();
    const keyboardService = useKeyboardService();
    const plainEditorRef = useRef(null);
    const isUniversalSearchEnabled = application.featuresController.isUniversalSearchEnabled();
    const noteUuid = props.controller.item.uuid;
    const controller = useMemo(() => {
        if (!isUniversalSearchEnabled) {
            return undefined;
        }
        const provider = createPlainEditorUniversalSearchProvider({
            getEditor: () => { var _a; return (_a = plainEditorRef.current) !== null && _a !== void 0 ? _a : undefined; },
        });
        return new UniversalSearchController(provider);
    }, [isUniversalSearchEnabled]);
    useEffect(() => {
        controller === null || controller === void 0 ? void 0 : controller.resetContext();
    }, [noteUuid, controller]);
    useImperativeHandle(ref, () => ({
        focus: () => { var _a; return (_a = plainEditorRef.current) === null || _a === void 0 ? void 0 : _a.focus(); },
        getText: () => { var _a, _b; return (_b = (_a = plainEditorRef.current) === null || _a === void 0 ? void 0 : _a.getText()) !== null && _b !== void 0 ? _b : ''; },
        getTextarea: () => { var _a, _b; return (_b = (_a = plainEditorRef.current) === null || _a === void 0 ? void 0 : _a.getTextarea()) !== null && _b !== void 0 ? _b : null; },
        setSelection: (start, end, options) => { var _a; return (_a = plainEditorRef.current) === null || _a === void 0 ? void 0 : _a.setSelection(start, end, options); },
        replaceRange: async (start, end, replacement) => {
            var _a;
            await ((_a = plainEditorRef.current) === null || _a === void 0 ? void 0 : _a.replaceRange(start, end, replacement));
        },
        replaceAllRanges: async (ranges, replacement) => {
            var _a;
            await ((_a = plainEditorRef.current) === null || _a === void 0 ? void 0 : _a.replaceAllRanges(ranges, replacement));
        },
        onTextChange: (callback) => { var _a, _b; return (_b = (_a = plainEditorRef.current) === null || _a === void 0 ? void 0 : _a.onTextChange(callback)) !== null && _b !== void 0 ? _b : (() => undefined); },
    }), []);
    useEffect(() => {
        return () => {
            controller === null || controller === void 0 ? void 0 : controller.deinit();
        };
    }, [controller]);
    const wasSearchOpenRef = useRef(false);
    useEffect(() => {
        var _a, _b;
        if (wasSearchOpenRef.current && !(controller === null || controller === void 0 ? void 0 : controller.isOpen)) {
            (_a = plainEditorRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
        wasSearchOpenRef.current = (_b = controller === null || controller === void 0 ? void 0 : controller.isOpen) !== null && _b !== void 0 ? _b : false;
    }, [controller === null || controller === void 0 ? void 0 : controller.isOpen]);
    const handleTextChange = useCallback(() => {
        if (!(controller === null || controller === void 0 ? void 0 : controller.isOpen)) {
            return;
        }
        controller.refreshSearch();
    }, [controller]);
    useEffect(() => {
        if (!controller || !isUniversalSearchEnabled) {
            return;
        }
        return registerUniversalSearchKeyboardHandlers(keyboardService, controller);
    }, [controller, isUniversalSearchEnabled, keyboardService]);
    const setPlainEditorRef = useCallback((editor) => {
        plainEditorRef.current = editor;
    }, []);
    const search = controller
        ? {
            isOpen: controller.isOpen,
            query: controller.query,
            isCaseSensitive: controller.isCaseSensitive,
            shouldHighlightAll: controller.shouldHighlightAll,
            activeMatch: (_a = controller.currentResult) === null || _a === void 0 ? void 0 : _a.payload,
        }
        : undefined;
    const searchToggleShortcut = keyboardStringForShortcut(keyboardService.keyboardShortcutForCommand(UNIVERSAL_TOGGLE_SEARCH));
    const toggleReplaceShortcut = keyboardStringForShortcut(keyboardService.keyboardShortcutForCommand(UNIVERSAL_SEARCH_TOGGLE_REPLACE_MODE));
    const caseSensitivityShortcut = keyboardStringForShortcut(keyboardService.keyboardShortcutForCommand(UNIVERSAL_SEARCH_TOGGLE_CASE_SENSITIVE));
    if (!isUniversalSearchEnabled || !controller) {
        return _jsx(PlainEditor, { ...props, ref: setPlainEditorRef });
    }
    return (_jsxs("div", { className: "relative flex flex-grow flex-col", children: [_jsx(UniversalSearchShell, { controller: controller, locked: props.locked, closeShortcut: searchToggleShortcut, replaceShortcut: toggleReplaceShortcut, caseSensitivityShortcut: caseSensitivityShortcut }), _jsx(PlainEditor, { ...props, ref: setPlainEditorRef, isSearchMode: true, onTextChange: handleTextChange, search: search })] }));
}));
